import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { AlertTriangle, Filter, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const severityColor = {
  CRITICAL: 'bg-red-500/10 text-red-400 border-red-500/20',
  HIGH:     'bg-orange-500/10 text-orange-400 border-orange-500/20',
  MEDIUM:   'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  LOW:      'bg-green-500/10 text-green-400 border-green-500/20',
};

const statusColor = {
  pending_review: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  approved:       'bg-green-500/10 text-green-400 border-green-500/20',
  rejected:       'bg-red-500/10 text-red-400 border-red-500/20',
  escalated:      'bg-purple-500/10 text-purple-400 border-purple-500/20',
};

export default function FraudCasesPage() {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [cases, setCases] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [days, setDays] = useState(30);
  const [offset, setOffset] = useState(0);
  const PAGE_SIZE = 50;

  const fetchCases = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days, offset, limit: PAGE_SIZE });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (severityFilter !== 'all') params.set('severity', severityFilter);
      const res = await fetch(`${API_URL}/api/admin/fraud/cases?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCases(data.cases || []);
        setTotal(data.total || 0);
      }
    } catch (e) { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { fetchCases(); }, [statusFilter, severityFilter, days, offset]);

  return (
    <div className="space-y-6" data-testid="fraud-cases-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl font-semibold flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-yellow-400" strokeWidth={1.5} />
            Fraud Cases
          </h1>
          <p className="text-muted-foreground text-sm">{total} case(s) matching current filters</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <select
          className="bg-muted border border-white/10 text-sm rounded-lg px-3 py-1.5"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setOffset(0); }}
        >
          <option value="all">All Status</option>
          <option value="pending_review">Pending Review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="escalated">Escalated</option>
        </select>
        <select
          className="bg-muted border border-white/10 text-sm rounded-lg px-3 py-1.5"
          value={severityFilter}
          onChange={(e) => { setSeverityFilter(e.target.value); setOffset(0); }}
        >
          <option value="all">All Severity</option>
          <option value="CRITICAL">Critical</option>
          <option value="HIGH">High</option>
          <option value="MEDIUM">Medium</option>
          <option value="LOW">Low</option>
        </select>
        <select
          className="bg-muted border border-white/10 text-sm rounded-lg px-3 py-1.5"
          value={days}
          onChange={(e) => { setDays(Number(e.target.value)); setOffset(0); }}
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground text-xs uppercase tracking-wide">
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Score</th>
              <th className="px-4 py-3">Severity</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">IP</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
            ) : cases.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No cases found.</td></tr>
            ) : cases.map((c) => (
              <tr key={c.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3">
                  <div className="font-medium">{c.name || '—'}</div>
                  <div className="text-xs text-muted-foreground">{c.email || '—'}</div>
                </td>
                <td className="px-4 py-3 font-semibold">{c.fraud_score}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${severityColor[c.severity] || ''}`}>{c.severity}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${statusColor[c.status] || ''}`}>{c.status?.replace('_', ' ')}</span>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{c.ip_address || '—'}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}</td>
                <td className="px-4 py-3">
                  <Link to={`/admin/fraud/cases/${c.id}`}>
                    <Button variant="outline" size="sm" className="gap-1">
                      Review <ExternalLink className="h-3 w-3" />
                    </Button>
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Prev</Button>
            <Button variant="outline" size="sm" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
